import { useState, useEffect } from "react";
import { Button, List } from "@theorgchartdev/storybook";
import SectionCard, { SectionCardGroup } from "../../../../Composites/SectionCard/SectionCard";
import { GetSSOConfigs, GetUserList, LoadDirectory, UpdateSSOConfigs } from "@/Utils/Promises";
import { BuildUserList, BuildSSOList, removeSSOMappingAt } from "./AuthorizationUtils";
import { SSOConfigModal } from "./SSOConfigModal/SSOConfigModal";
import { LinkUserModal } from "./LinkUserModal/LinkUserModal";
import { GeneralSection } from "./GeneralSection/GeneralSection";
import { AutoAssignmentConfiguration as AutoAssignment } from "./AutoAssignmentSection/AutoAssignment";
import { SharePointConfig } from "./SharePointConfig/SharePointConfig";
import { ScimTokenPanel } from "./ScimTokenPanel/ScimTokenPanel";
import { ScimSamlConflictBanner } from "./ScimSamlConflictBanner/ScimSamlConflictBanner";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { appstr } from "@coreapp/utils/appstr";
import { getFeatureAccess, isSharepointExportEnabled } from "@/Utils/Misc";
import { enumBrand, enumFeaturesList } from "@coreapp/constants";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import UpgradeCard from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeCard";
import { UpgradeInfoCard } from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeInfoCard";
import { gWhiteLabel } from "@coreapp/whitelabel/brand";

export const AuthorizationConfiguration = ({ accountConfig, handleUpdate }) => {
  const [users, setUsers] = useState([]);
  const [ssoMapping, setSSOMapping] = useState([]);
  const [groups, setGroups] = useState([]);
  const [localMetadata, setLocalMetadata] = useState([]);
  const [selectedUser, setSelectUser] = useState(null);
  const [selectedSSO, setSelectedSSO] = useState(null);
  const [createSSO, setCreateSSO] = useState(false);
  const [showUserConfig, setShowUserConfig] = useState(false);
  const [showSSOConfig, setShowSSOConfig] = useState(false);
  const [showSharePoint, setShowSharePoint] = useState(false);
  const [doReload, setDoReload] = useState(false);

  const { showPricingAndPackaging } = usePermissionCheck();

  const showSCIMSection = gWhiteLabel !== enumBrand.isolved;

  const showSP = isSharepointExportEnabled();

  const LoadUsers = async () => {
    const { userList, groupInfo } = await GetUserList();
    setUsers(userList);
    setGroups(groupInfo);
  };

  const LoadSSOMapping = async () => {
    const mapping = await GetSSOConfigs();
    if (mapping) setSSOMapping(mapping);
  };

  const LoadLocalSSOMetadata = async () => {
    const { success, processedResults } = await LoadDirectory("System", "ssoConfig");
    if (success) setLocalMetadata(processedResults);
  };

  const EditLinkUser = user => {
    setSelectUser(user);
    setShowUserConfig(true);
  };

  const HandleDeleteSSOConfig = async ssoIdx => {
    const nextMapping = removeSSOMappingAt(ssoMapping, ssoIdx);
    try {
      const { success, mapping } = await UpdateSSOConfigs(nextMapping);
      if (success) setSSOMapping(mapping);
    } catch {
      setDoReload(true);
    }
  };

  const HandleSSOAction = (key, ssoIdx) => {
    switch (key) {
      case "edit":
        setSelectedSSO(ssoIdx);
        setShowSSOConfig(true);
        break;
      case "delete":
        HandleDeleteSSOConfig(ssoIdx);
        break;
    }
  };

  const CreateNewSSOConfig = () => {
    setCreateSSO(true);
    setShowSSOConfig(true);
  };

  const CloseSSOModal = () => {
    setShowSSOConfig(false);
    setSelectedSSO(null);
    setCreateSSO(false);
  };

  const CloseLinkUserModal = reload => {
    setSelectUser(null);
    setShowUserConfig(false);
    if (reload) LoadUsers();
  };

  useEffect(() => {
    LoadUsers();
    LoadSSOMapping();
    LoadLocalSSOMetadata();
    setDoReload(false);
  }, [doReload]);

  if (!accountConfig) return;

  let credsObj = accountConfig.security?.creds || null;
  let sharePointObj = credsObj ? credsObj?.sharepoint : null;

  const DeleteSharepointConfig = () => {
    const newCreds = { ...(accountConfig.security?.creds || {}) };
    delete newCreds.sharepoint;
    const updatedAccountConfig = {
      ...accountConfig,
      security: { ...(accountConfig.security || {}), creds: newCreds }
    };
    handleUpdate(updatedAccountConfig, "security");
  };

  const UpdateSharePointConfig = newCreds => {
    const mergedCreds = { ...(accountConfig.security?.creds || {}), sharepoint: newCreds };
    const updatedAccountConfig = {
      ...accountConfig,
      security: { ...(accountConfig.security || {}), creds: mergedCreds }
    };
    handleUpdate(updatedAccountConfig, "security");
    setShowSharePoint(false);
  };

  const addSSOBtn = (
    <Button
      modifier={"secondary"}
      className={"oc-special-secondary"}
      iconProps={{ name: "plus" }}
      onClick={() => CreateNewSSOConfig()}
      disabled={!getFeatureAccess(enumFeaturesList.SSOAutoPro)}
      dataTestId="account-settings-authorization-add-sso-btn"
    >
      {appstr("Add SSO Configuration")}
    </Button>
  );

  let sharePointContent = sharePointObj
    ? [
        {
          content: (
            <ActionListItem
              label={`https://${sharePointObj.domain}.sharepoint.com`}
              actions={[
                {
                  iconName: "gear",
                  onClick: () => setShowSharePoint(true),
                  dataTestId: "account-settings-authorization-sharepoint-edit-btn"
                },
                {
                  iconName: "trash",
                  onClick: () => DeleteSharepointConfig(),
                  dataTestId: "account-settings-authorization-sharepoint-delete-btn"
                }
              ]}
            />
          ),
          divider: false,
          value: sharePointObj.domain
        }
      ]
    : [];

  let customCardStyle = {};
  if (showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.groupAutoAssignment)) {
    customCardStyle = { background: "var(--bg-brand-secondary)" };
  }

  const scimUpgradeMessageDynamicText = {
    template: appstr("SCIMUpgradeTemplate"),
    formatConfig: { bold1: { type: "bold" }, bold2: { type: "bold" } },
    replaceDict: {
      bold1: appstr("SCIMUpgradeBold1"),
      bold2: appstr("SCIMUpgradeBold2")
    }
  };

  return (
    <SectionCardGroup>
      <SectionCard title={appstr("General")}>
        <GeneralSection accountConfig={accountConfig} handleUpdate={handleUpdate} />
      </SectionCard>

      <SectionCard title={appstr("Link User Configuration")}>
        <List
          enableVirtualization={false}
          options={BuildUserList(users, EditLinkUser)}
          listWidth="fill"
          optionSize={"large"}
          showCheck={false}
          value={null}
        />
        {selectedUser && (
          <LinkUserModal show={showUserConfig} user={selectedUser} onClose={CloseLinkUserModal} />
        )}
      </SectionCard>

      <SectionCard title={appstr("SSO Configuration")} titleContent={addSSOBtn}>
        <List
          enableVirtualization={false}
          options={BuildSSOList(ssoMapping, HandleSSOAction)}
          listWidth="fill"
          optionSize={"large"}
          showCheck={false}
          value={null}
        />
        {(selectedSSO || createSSO) && (
          <SSOConfigModal
            show={showSSOConfig}
            existingConfig={selectedSSO}
            ssoMapping={ssoMapping}
            groups={groups}
            localMetadata={localMetadata}
            onClose={CloseSSOModal}
            newConfig={createSSO}
            setDoReload={setDoReload}
            reloadFiles={LoadLocalSSOMetadata}
          />
        )}
      </SectionCard>

      {showSP && (
        <SectionCard title={appstr("ConfigureSharePoint")}>
          {!sharePointObj ? (
            <Button
              modifier={"link"}
              onClick={() => setShowSharePoint(true)}
              dataTestId="account-settings-authorization-configure-sharepoint-link"
            >
              {appstr("NoSharepointConfig")}
            </Button>
          ) : (
            <List
              enableVirtualization={false}
              options={sharePointContent}
              listWidth="fill"
              optionSize={"medium"}
              showCheck={false}
              value={null}
            />
          )}
          {showSharePoint && (
            <SharePointConfig
              show={showSharePoint}
              onClose={() => setShowSharePoint(false)}
              updateCreds={UpdateSharePointConfig}
              existingCredsObj={sharePointObj}
            />
          )}
        </SectionCard>
      )}

      <SectionCard
        title={appstr("Auto-Assignment Configuration")}
        customCardStyle={customCardStyle}
      >
        <AutoAssignment accountConfig={accountConfig} handleUpdate={handleUpdate} />
      </SectionCard>

      {showSCIMSection && getFeatureAccess(enumFeaturesList.SCIM) && (
        <SectionCard title={appstr("SCIMSectionTitle")}>
          <ScimSamlConflictBanner visible={ssoMapping?.some(c => c.enabled && c.autoProvision)} />
          <ScimTokenPanel />
        </SectionCard>
      )}

      {showSCIMSection && showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.SCIM) && (
        <SectionCard
          title={appstr("SCIMSectionTitle")}
          customCardStyle={{ background: "var(--bg-brand-secondary)" }}
        >
          <UpgradeCard iconName={"users-gear-regular"} featureKey={enumFeaturesList.SCIM}>
            <UpgradeInfoCard.Body dynamic={scimUpgradeMessageDynamicText}>
              {appstr("SCIMUpgradeBody")}
            </UpgradeInfoCard.Body>
          </UpgradeCard>
        </SectionCard>
      )}
    </SectionCardGroup>
  );
};
